import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getPreferences, savePreferences } from '../api/preferences';
import { updateNativeStatusBar } from '../utils/statusBar';
import {
  getSecuritySettings,
  updateSecuritySettings,
  verifySecurityTwoFactor,
  disableSecurityTwoFactor,
  regenerateRecoveryCodes,
  recordSecurityActivity,
} from '../api/security';

const SettingsContext = createContext(null);

const defaults = {
  dateFormat: 'DD/MM/YYYY',
  theme: 'system',
  language: 'en',
};

const securityDefaults = {
  two_factor_enabled: false,
  login_alerts_enabled: true,
  session_timeout_minutes: 480,
};

const normalize = (data = {}) => ({
  dateFormat: data.date_format || data.dateFormat || defaults.dateFormat,
  theme: data.theme || defaults.theme,
  language: data.language || defaults.language,
});

const resolveTheme = (theme) => {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
};

export const SettingsProvider = ({ children }) => {
  const [preferences, setPreferences] = useState(() => {
    try {
      return normalize(JSON.parse(localStorage.getItem('user_preferences') || '{}'));
    } catch {
      return defaults;
    }
  });
  const [security, setSecurity] = useState(securityDefaults);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [securitySaving, setSecuritySaving] = useState(false);
  const [securitySetup, setSecuritySetup] = useState(null);

  const applyTheme = useCallback((theme) => {
    const activeTheme = resolveTheme(theme);
    const isDark = activeTheme === 'dark';
    document.documentElement.classList.toggle('dark', isDark);
    updateNativeStatusBar(isDark);
    localStorage.setItem('theme', theme);
    localStorage.setItem('resolved_theme', activeTheme);
  }, []);

  const loadPreferences = useCallback(async () => {
    const response = await getPreferences();
    const next = normalize(response?.data || response);
    setPreferences(next);
    localStorage.setItem('user_preferences', JSON.stringify(next));
    applyTheme(next.theme);
    return next;
  }, [applyTheme]);

  const save = useCallback(async (payload) => {
    const response = await savePreferences(payload);
    const next = normalize(response?.data || response);
    setPreferences(next);
    localStorage.setItem('user_preferences', JSON.stringify(next));
    applyTheme(next.theme);
    return next;
  }, [applyTheme]);

  const loadSecuritySettings = useCallback(async () => {
    setSecurityLoading(true);
    try {
      const response = await getSecuritySettings();
      const next = { ...securityDefaults, ...(response?.data || response) };
      setSecurity(next);
      localStorage.setItem('security_preferences', JSON.stringify(next));
      return next;
    } finally {
      setSecurityLoading(false);
    }
  }, []);

  const saveSecuritySettings = useCallback(async (payload) => {
    setSecuritySaving(true);
    try {
      const response = await updateSecuritySettings(payload);
      const next = { ...securityDefaults, ...(response?.data || response) };
      setSecurity(prev => ({ ...prev, ...next }));

      const requiresTwoFactorSetup = response?.meta?.requires_two_factor_setup;
      if (requiresTwoFactorSetup) {
        setSecuritySetup(response?.meta?.two_factor_setup || null);
      } else {
        setSecuritySetup(null);
      }

      localStorage.setItem('security_preferences', JSON.stringify({ ...securityDefaults, ...next }));
      return response;
    } finally {
      setSecuritySaving(false);
    }
  }, []);

  const verifyTwoFactorSetup = useCallback(async (payload) => {
    const response = await verifySecurityTwoFactor(payload);
    const next = { ...security, two_factor_enabled: true };
    setSecurity(next);
    setSecuritySetup(null);
    localStorage.setItem('security_preferences', JSON.stringify(next));
    return response;
  }, [security]);

  const disableTwoFactor = useCallback(async (payload) => {
    const response = await disableSecurityTwoFactor(payload);
    const next = { ...security, two_factor_enabled: false };
    setSecurity(next);
    localStorage.setItem('security_preferences', JSON.stringify(next));
    return response;
  }, [security]);

  const regenerateCodes = useCallback(async (payload) => {
    return regenerateRecoveryCodes(payload);
  }, []);

  const pingSecurityActivity = useCallback(async (payload = {}) => {
    try {
      return await recordSecurityActivity(payload);
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    applyTheme(preferences.theme);
    if (preferences.theme === 'system') {
      const media = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => applyTheme('system');
      media.addEventListener?.('change', handler);
      return () => media.removeEventListener?.('change', handler);
    }
  }, [preferences.theme, applyTheme]);

  const value = useMemo(() => ({
    ...preferences,
    loadPreferences,
    savePreferences: save,
    applyTheme,
    setPreferences,
    security,
    securityLoading,
    securitySaving,
    securitySetup,
    loadSecuritySettings,
    saveSecuritySettings,
    verifyTwoFactorSetup,
    disableTwoFactor,
    regenerateRecoveryCodes: regenerateCodes,
    recordSecurityActivity: pingSecurityActivity,
    setSecuritySetup,
    setSecurity,
  }), [
    preferences,
    loadPreferences,
    save,
    applyTheme,
    security,
    securityLoading,
    securitySaving,
    securitySetup,
    loadSecuritySettings,
    saveSecuritySettings,
    verifyTwoFactorSetup,
    disableTwoFactor,
    regenerateCodes,
    pingSecurityActivity,
  ]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) throw new Error('useSettings must be used within SettingsProvider');
  return context;
};
